/**
 * Display locale for all formatters below. The I18nProvider calls
 * `setFormatLocale` whenever the chosen locale changes, so money and Jalali
 * dates follow the panel's language (Perso-Arabic digits for Dari/Pashto,
 * Latin for English) without threading a locale argument through every call
 * site — the same reason `document.dir` is set globally.
 */
type FormatLocale = "fa" | "ps" | "en";
let activeLocale: FormatLocale = "fa";
export function setFormatLocale(locale: FormatLocale): void {
  activeLocale = locale;
}

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
/** Latin digits → Perso-Arabic, except under English. No grouping added. */
function localizeDigits(value: number | string): string {
  const s = String(value);
  return activeLocale === "en" ? s : s.replace(/[0-9]/g, (c) => FA_DIGITS[+c]);
}

/** Money is integer minor units everywhere (docs/02 §6). */
export function formatMoney(amountMinor: number, currency = "AFN"): string {
  const major = Math.round(amountMinor / 100);
  // toLocaleString picks the digits and grouping separators for the locale:
  // "fa-AF" → "۱۸۲٬۰۰۰", "en-US" → "182,000". ؋ is script-neutral, kept in both.
  const grouped = major.toLocaleString(activeLocale === "en" ? "en-US" : "fa-AF");
  return currency === "AFN" ? `${grouped} ؋` : `$${grouped}`;
}

const DARI_MONTHS = [
  "حمل", "ثور", "جوزا", "سرطان", "اسد", "سنبله",
  "میزان", "عقرب", "قوس", "جدی", "دلو", "حوت",
];

// Afghan Jalali month names, mirroring android core/common JalaliDate.
const PASHTO_MONTHS = [
  "وری", "غویی", "غبرګولی", "چنګاښ", "زمری", "وږی",
  "تله", "لړم", "لیندۍ", "مرغومی", "سلواغه", "کب",
];
const ENGLISH_MONTHS = [
  "Hamal", "Sawr", "Jawza", "Saratan", "Asad", "Sonbola",
  "Mizan", "Aqrab", "Qaws", "Jadi", "Dalw", "Hoot",
];

function monthNames(): string[] {
  return activeLocale === "ps" ? PASHTO_MONTHS : activeLocale === "en" ? ENGLISH_MONTHS : DARI_MONTHS;
}

const G_DAYS = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];

export function gregorianToJalali(gy: number, gm: number, gd: number) {
  const gy2 = gm > 2 ? gy + 1 : gy;
  let days =
    355666 + 365 * gy + Math.floor((gy2 + 3) / 4) - Math.floor((gy2 + 99) / 100) +
    Math.floor((gy2 + 399) / 400) + gd + G_DAYS[gm - 1];
  let jy = -1595 + 33 * Math.floor(days / 12053);
  days %= 12053;
  jy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    jy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  const jm = days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
  return { jy, jm, jd };
}

/** "2026-08-21" → "۳۰ اسد ۱۴۰۵" (fa/ps) or "30 Asad 1405" (en). */
export function formatJalali(isoDate: string): string {
  const [gy, gm, gd] = isoDate.split("-").map(Number);
  const { jy, jm, jd } = gregorianToJalali(gy, gm, gd);
  return `${localizeDigits(jd)} ${monthNames()[jm - 1]} ${localizeDigits(jy)}`;
}

/**
 * "YYYY-MM-DD" for an instant, resolved in Kabul time.
 *
 * Slicing `toISOString()` reads the UTC day, which is the previous day for
 * anything happening in Kabul before 04:30 — a contract issued at 02:00 would
 * be dated yesterday, and "today" would be wrong for the first 4½ hours of
 * every Kabul morning. Asia/Kabul is +04:30 and has no DST.
 */
export function kabulIsoDay(date: Date): string {
  // en-CA renders as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kabul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/** Today in Kabul — what "today" means to every hall this product serves. */
export function kabulToday(): string {
  return kabulIsoDay(new Date());
}

/** Jalali date for an *instant* (a timestamp), resolved in Kabul time. */
export function formatJalaliInstant(date: Date): string {
  return formatJalali(kabulIsoDay(date));
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Inverse of [gregorianToJalali] — same algorithm family as android
 * core/common JalaliDate.toGregorian, so the two round-trip. Pure arithmetic:
 * no Date object, so no timezone can shift the day.
 */
export function jalaliToGregorian(jy: number, jm: number, jd: number) {
  const jy1 = jy + 1595;
  let days =
    -355668 + 365 * jy1 + Math.floor(jy1 / 33) * 8 + Math.floor(((jy1 % 33) + 3) / 4) + jd +
    (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
  let gy = 400 * Math.floor(days / 146097);
  days %= 146097;
  if (days > 36524) {
    days--;
    gy += 100 * Math.floor(days / 36524);
    days %= 36524;
    if (days >= 365) days++;
  }
  gy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    gy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  let gd = days + 1;
  const leap = (gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0;
  const monthDays = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let gm = 1;
  while (gm <= 12 && gd > monthDays[gm - 1]) {
    gd -= monthDays[gm - 1];
    gm++;
  }
  return { gy, gm, gd };
}

/** Jalali (y, m, d) → the stored ISO Gregorian "YYYY-MM-DD". */
export function jalaliToIso(jy: number, jm: number, jd: number): string {
  const { gy, gm, gd } = jalaliToGregorian(jy, jm, jd);
  return `${gy}-${pad2(gm)}-${pad2(gd)}`;
}

/** ISO "YYYY-MM-DD" → Jalali parts. */
export function isoToJalali(isoDate: string) {
  const [gy, gm, gd] = isoDate.split("-").map(Number);
  return gregorianToJalali(gy, gm, gd);
}

/** 31 days for months 1–6, 30 for 7–11, حوت 29 or 30 (leap — checked by round-trip). */
export function jalaliMonthLength(jy: number, jm: number): number {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  const g = jalaliToGregorian(jy, 12, 30);
  const back = gregorianToJalali(g.gy, g.gm, g.gd);
  return back.jy === jy && back.jm === 12 && back.jd === 30 ? 30 : 29;
}

/** Column in an Afghan week (0 = شنبه … 6 = جمعه) for an ISO date. Timezone-free. */
export function weekdayIndexSatFirst(isoDate: string): number {
  const [y, m, d] = isoDate.split("-").map(Number);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 1) % 7;
}

/** Afghan Jalali month name in the panel's language. */
export function jalaliMonthName(jm: number): string {
  return monthNames()[jm - 1];
}

/** A count or quantity in the panel's digits, grouped: ۱٬۲۰۰ / 1,200. */
export function formatNumber(n: number): string {
  return n.toLocaleString(activeLocale === "en" ? "en-US" : "fa-AF");
}

/** Digits only, no grouping — for years, periods and codes: ۱۴۰۵-۰۷. */
export function formatDigits(value: number | string): string {
  return localizeDigits(value);
}

/** Firestore serverTimestamp serializes over REST as { _seconds }. */
type FsTimestamp = { _seconds: number };

/**
 * Same, for the two shapes the REST API hands back. Routed through
 * formatJalaliInstant so it resolves in Kabul rather than slicing the UTC day.
 */
export function formatJalaliTs(value: string | FsTimestamp | null | undefined): string {
  if (!value) return "—";
  const date = typeof value === "string" ? new Date(value) : new Date(value._seconds * 1000);
  return formatJalaliInstant(date);
}

export function localized(
  name: Record<string, string> | undefined,
  locale: FormatLocale = activeLocale,
): string {
  if (!name) return "";
  return name[locale] ?? name.fa ?? name.en ?? Object.values(name)[0] ?? "";
}
